import Axios from 'axios'
import appConfig from "./config"
import { logoutWhenTokenExp } from "services/logoutTokenExp";
import { endSession } from "services/session";
import { wrapAxiosError } from './axiosRejectWrap';
import { emitApiError } from './apiErrorBus';
import log from './logger';
import { serializeParams } from './serializeParams';

let baseUrl = appConfig.apiUrl;

let axios = Axios.create({
  withCredentials: false,
  baseURL: baseUrl,
  paramsSerializer: { serialize: serializeParams },
})

// A bare axios instance for the refresh call itself. Using the main one would run
// the interceptors again and let a failing refresh recurse.
const refreshClient = Axios.create({ baseURL: baseUrl });

const REFRESH_PATH = '/token/refresh/';

// One refresh at a time. The endpoint rotates the refresh token, so two concurrent
// 401s each starting their own refresh meant the second one presented a token the
// server had already invalidated, and the user was thrown out mid-task.
let refreshPromise = null;

const refreshTokens = () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    return Promise.reject(new Error("No refresh token"));
  }

  refreshPromise = refreshClient
    .post(REFRESH_PATH, { refresh: refreshToken })
    .then((res) => {
      if (res.status !== 200 || !res.data?.access) {
        throw new Error("Token refresh failed");
      }
      localStorage.setItem("accessToken", res.data.access);
      if (res.data.refresh) {
        localStorage.setItem("refreshToken", res.data.refresh);
      }
      logoutWhenTokenExp();
      log("Access and refresh token refreshed");
      return res.data.access;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

//request interceptor to add the auth token header to requests
axios.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      config.headers["Authorization"] = `jwt ${accessToken}`;
    }
    // Language
    const language = localStorage.getItem("language", "fi");
    config.headers["Accept-Language"] = language

    return config;
  },
  (error) => {
    return Promise.reject(wrapAxiosError(error));
  }
);

//response interceptor to refresh token on receiving token expired error
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // 403 means the server refused the action, not the session. There is nothing to
    // retry, so hand it to the notification layer or the view keeps its spinner and
    // the user gets no explanation. (Sovellon #2260, read_only role.)
    if (status === 403) {
      emitApiError({ status: 403, data: error.response?.data, url: originalRequest?.url });
      return Promise.reject(wrapAxiosError(error));
    }

    if (status !== 401) {
      return Promise.reject(wrapAxiosError(error));
    }

    // Belt and braces. The refresh runs on refreshClient, which has no interceptors,
    // so a 401 from it does not reach here today — but if someone later routes the
    // refresh through this instance, recursing would be the wrong answer.
    if (originalRequest?.url?.includes(REFRESH_PATH)) {
      endSession();
      return Promise.reject(wrapAxiosError(error));
    }

    if (!localStorage.getItem("refreshToken")) {
      if (localStorage.getItem("accessToken")) {
        log("No refresh token, ending session");
        endSession();
      }
      return Promise.reject(wrapAxiosError(error));
    }

    if (originalRequest._retry) {
      return Promise.reject(wrapAxiosError(error));
    }
    originalRequest._retry = true;

    return refreshTokens().then(
      () => axios(originalRequest),
      (refreshError) => {
        log("Token refresh failed", refreshError);
        endSession();
        return Promise.reject(wrapAxiosError(error));
      }
    );
  }
);

/**
 * Refresh the session on demand.
 *
 * Used by the expiry warning so the user can stay signed in without losing the form
 * they are in the middle of. Shares the same single-flight promise as the
 * interceptor, so pressing the button while a request is already refreshing joins
 * that refresh instead of starting a second one.
 */
export const refreshSession = () => refreshTokens();

axios.defaults.xsrfCookieName = 'csrftoken'
axios.defaults.xsrfHeaderName = 'X-CSRFToken'

export default axios;
